/**
 * The browser clipboard, for the export menu's Copy and Paste JSON.
 * Browsers only let a page read the clipboard on a click, or, in Chrome, once
 * the user has granted clipboard permission. So the clipboard can only be
 * previewed where reading is already allowed; everywhere else it is unknown
 * and the click itself reads it.
 */
import { useEffect, useState } from 'react';

/** Put text on the clipboard; false when the browser refused. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** The clipboard's text, read on a click; null when the browser refused. */
export async function readClipboardText(): Promise<string | null> {
  try {
    return await navigator.clipboard.readText();
  } catch {
    return null;
  }
}

async function readClipboardIfAllowed(): Promise<string | null> {
  try {
    const permission = await navigator.permissions.query({ name: 'clipboard-read' as PermissionName });
    return permission.state === 'granted' ? await navigator.clipboard.readText() : null;
  } catch {
    return null;
  }
}

/** The clipboard's text where the browser lets it be read without a click; null otherwise. Rechecked whenever the window regains focus. */
export function useClipboardText(active: boolean): string | null {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    if (!active) return;
    let current = true;
    const check = () => {
      void readClipboardIfAllowed().then(clipboard => {
        if (current) setText(clipboard);
      });
    };
    check();
    window.addEventListener('focus', check);
    return () => {
      current = false;
      window.removeEventListener('focus', check);
    };
  }, [active]);
  return text;
}
